import {
  MutationCache,
  QueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { toaster } from './toaster';

// Type augmentation for mutation meta
declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: CustomMutationMeta;
  }
}

interface CustomMutationMeta {
  skipGlobalErrorHandler?: boolean;
}

function extractErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const data: unknown = error.response?.data;

    if (typeof data === 'object' && data !== null) {
      if ('error' in data && typeof data.error === 'string') return data.error;
      if ('message' in data && typeof data.message === 'string')
        return data.message;
    }

    if (typeof data === 'string') return data;

    return error.message;
  }

  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;

  return 'An unexpected error occurred';
}

export type MutationConfig<
  MutationFnType extends (...args: any[]) => Promise<unknown>,
> = UseMutationOptions<
  Awaited<ReturnType<MutationFnType>>,
  Error,
  Parameters<MutationFnType>[0]
>;

// Create a QueryClient instance
export const queryClient: QueryClient = new QueryClient({
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      if (mutation.meta?.skipGlobalErrorHandler) {
        return;
      }
      const message = extractErrorMessage(error);
      toaster.error(message);
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 60 * 2 * 1000, // 2 minutes
      refetchOnWindowFocus: false,
    },
  },
});
